import { ref, readonly, computed } from 'vue';

/**
 * Composable para acessar configurações de runtime
 * Permite injeção de variáveis de ambiente após o build
 */
export function useRuntimeConfig() {
  // Fallback para configurações padrão caso o arquivo config.js não esteja disponível
  const defaultConfig = {
    API_URL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
    S3_IMAGEM_URL: import.meta.env.VITE_S3_IMAGEM_URL || 'http://localhost:9000/arlo-imagens/',
    S3_ASSETS_LOGO: import.meta.env.VITE_S3_ASSETS_LOGO || 'http://localhost:9000/arlo-imagens/assets/koda_logo_horizontal_nome.svg',
    APP_DEBUG: import.meta.env.VITE_APP_DEBUG === true || import.meta.env.VITE_APP_DEBUG === 'true' || false,
    APP_ENV: import.meta.env.VITE_APP_ENV || 'local',
    RECAPTCHA_SITE_KEY: import.meta.env.VITE_RECAPTCHA_SITE_KEY || '',
    TEM_TERMO_AUTORIZACAO: false,
    MATRICULA_ALUNO_OBRIGATORIA: false,
    EXIBIR_SELECAO_PAGAMENTO: false,
    CADASTROPF_DADOS_BANCARIOS_OBRIGATORIO: true,
    CADASTROPF_DADOS_SOCIOECONOMICOS_OBRIGATORIO: true,
    BUSCARPF_APENAS_POR_CPF: false,
    MUNICIPAL_POR_PROJETO: false,
    EXPORTAR_DDS: false,
    MENU_ADMINISTRATIVO: false,
    MENU_PROCESSOS_AUTORIZACOES: false,
    SOLICITACAO_ADMINISTRATIVA: false,
    TEM_CONTRATO_BOLSA_EVENTUAL_DINAMICO: false,
    PAGAMENTO_BOLSA_PARCIAL: true,
    TEM_CONVENIO_BB_ATIVO: false,
    IMPORTAR_OFX: false,
    MODULO_FORMULARIO: false,
    MENU_TUTORIAIS: false,
    PARECER_JURIDICO: false,
    INTERVALO_PROCESSOS_AGUARDANDO_SEGUNDOS: 30,
    INTERVALO_FORMULARIOS_PENDENTES_SEGUNDOS: 30,
    ENDERECO_AUTOCADASTRO_OBRIGATORIO: false,
    MODO_MANUTENCAO: false,
  };

  // Obtém configuração do runtime ou usa a configuração padrão
  const runtimeConfig = ref(window.__RUNTIME_CONFIG__ || defaultConfig);

  // Getter para API base URL
  const apiBaseUrl = computed(() => runtimeConfig.value.API_URL);

  // Getter para ambiente da aplicação
  const appEnv = computed(() => runtimeConfig.value.APP_ENV);

  // Flags de ambiente
  const isDev = computed(() => runtimeConfig.value.APP_ENV === 'local');
  const isProd = computed(() => runtimeConfig.value.APP_ENV !== 'local');

  // Getter para modo debug
  const isDebugEnabled = computed(() => runtimeConfig.value.APP_DEBUG);

  // Getter para configuração do S3
  const s3Config = computed(() => ({
    imagemUrl: runtimeConfig.value.S3_IMAGEM_URL,
    assetsLogo: runtimeConfig.value.S3_ASSETS_LOGO,
  }));

  // Getter para recaptcha site key
  const recaptchaSiteKey = computed(() => runtimeConfig.value.RECAPTCHA_SITE_KEY);

  // Feature flags
  const temTermoAutorizacao = computed(() => runtimeConfig.value.TEM_TERMO_AUTORIZACAO);
  const matriculaAlunoObrigatoria = computed(() => runtimeConfig.value.MATRICULA_ALUNO_OBRIGATORIA);
  const exibirSelecaoPagamento = computed(() => runtimeConfig.value.EXIBIR_SELECAO_PAGAMENTO);
  const cadastroPfDadosBancariosObrigatorio = computed(() => runtimeConfig.value.CADASTROPF_DADOS_BANCARIOS_OBRIGATORIO);
  const cadastroPfDadosSocioeconomicosObrigatorio = computed(() => runtimeConfig.value.CADASTROPF_DADOS_SOCIOECONOMICOS_OBRIGATORIO);
  const buscarPfApenasPorCpf = computed(() => runtimeConfig.value.BUSCARPF_APENAS_POR_CPF);
  const municipalPorProjeto = computed(() => runtimeConfig.value.MUNICIPAL_POR_PROJETO);
  const exibirBotaoExportarDDS = computed(() => runtimeConfig.value.EXPORTAR_DDS);
  const exibirMenuAdministrativo = computed(() => runtimeConfig.value.MENU_ADMINISTRATIVO);
  const exibirProcessosAutorizacoes = computed(() => runtimeConfig.value.MENU_PROCESSOS_AUTORIZACOES);
  const exibirSolicitacaoAdministrativa = computed(() => runtimeConfig.value.SOLICITACAO_ADMINISTRATIVA);
  const temContratoBolsaEventualDinamico = computed(() => runtimeConfig.value.TEM_CONTRATO_BOLSA_EVENTUAL_DINAMICO);
  const permitirPagamentoBolsaParcial = computed(() => runtimeConfig.value.PAGAMENTO_BOLSA_PARCIAL !== false);
  const temConvenioBBAtivo = computed(() => runtimeConfig.value.TEM_CONVENIO_BB_ATIVO);
  const exibirImportarOFX = computed(() => runtimeConfig.value.IMPORTAR_OFX);
  const moduloFormulario = computed(() => runtimeConfig.value.MODULO_FORMULARIO === true);
  const exibirMenuTutoriais = computed(() => runtimeConfig.value.MENU_TUTORIAIS === true);
  const exibirParecerJuridico = computed(() => runtimeConfig.value.PARECER_JURIDICO === true);
  const camposEnderecoAutocadastroObrigatorios = computed(() => runtimeConfig.value.ENDERECO_AUTOCADASTRO_OBRIGATORIO === true);
  const modoManutencao = computed(() => runtimeConfig.value.MODO_MANUTENCAO === true);

  // Função para obter qualquer configuração por chave
  const getConfig = (key, fallback = null) => {
    return runtimeConfig.value[key] ?? fallback;
  };

  // Função para atualizar configurações (útil para testes)
  const updateConfig = (newConfig) => {
    runtimeConfig.value = { ...runtimeConfig.value, ...newConfig };
  };

  // Função para verificar se uma configuração existe
  const hasConfig = (key) => {
    return key in runtimeConfig.value && runtimeConfig.value[key] !== undefined;
  };

  // Função para juntar a base URL com um caminho sem duplicar barras
  const joinUrl = (path) => {
    const base = runtimeConfig.value.API_URL.replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');
    return `${base}/${normalizedPath}`;
  };

  return {
    config: readonly(runtimeConfig),
    apiBaseUrl,
    appEnv,
    isDev,
    isProd,
    isDebugEnabled,
    s3Config,
    recaptchaSiteKey,
    temTermoAutorizacao,
    matriculaAlunoObrigatoria,
    exibirSelecaoPagamento,
    cadastroPfDadosBancariosObrigatorio,
    cadastroPfDadosSocioeconomicosObrigatorio,
    buscarPfApenasPorCpf,
    municipalPorProjeto,
    exibirBotaoExportarDDS,
    exibirMenuAdministrativo,
    exibirProcessosAutorizacoes,
    exibirSolicitacaoAdministrativa,
    temContratoBolsaEventualDinamico,
    permitirPagamentoBolsaParcial,
    temConvenioBBAtivo,
    exibirImportarOFX,
    moduloFormulario,
    exibirMenuTutoriais,
    exibirParecerJuridico,
    camposEnderecoAutocadastroObrigatorios,
    modoManutencao,
    getConfig,
    updateConfig,
    hasConfig,
    joinUrl,
  };
}